/**
 * EdenDate guards — turn unsupported server features into a clear message
 * instead of a raw ERR_ENTERPRISE_IS_BLOCKED rejection.
 */
import I18n from '@lib/langPack';
import rootScope from '@lib/rootScope';

function t(key: string, fallback?: string): string {
  try {
    const out = (I18n as any).format(key, true);
    if(typeof out === 'string' && out !== key) return out;
  } catch(e) {}
  return fallback || key;
}

const UNSUPPORTED = [
  'channels.createChannel',
  'phone.createConferenceCall',
  'phone.createGroupCall',
  'communities.getJoinedCommunities'
];

const patchedApis = new WeakSet<object>();
const patchedChats = new WeakSet<object>();

function showUnsupportedModal(feature: string) {
  const existing = document.querySelector('.eden-unsupported');
  if(existing) return;

  const wrap = document.createElement('div');
  wrap.className = 'eden-modal-overlay eden-unsupported';

  const card = document.createElement('div');
  card.className = 'eden-modal';

  const title = document.createElement('div');
  title.className = 'eden-modal-title';
  title.textContent = t('Eden.Unsupported.Title', '暂不支持');

  const text = document.createElement('div');
  text.className = 'eden-modal-text';
  text.textContent = t('Eden.Unsupported.Text', '当前服务器版本暂不支持该功能。');

  const ok = document.createElement('button');
  ok.className = 'eden-chip eden-chip-primary';
  ok.textContent = t('Eden.Close', '关闭');
  ok.addEventListener('click', () => wrap.remove());

  const actions = document.createElement('div');
  actions.className = 'eden-modal-actions';
  actions.appendChild(ok);

  card.appendChild(title);
  card.appendChild(text);
  card.appendChild(actions);
  wrap.appendChild(card);
  wrap.addEventListener('click', (e) => { if(e.target === wrap) wrap.remove(); });
  document.body.appendChild(wrap);
  console.warn('[eden] unsupported feature requested:', feature);
}

function showInfoModal(title: string, text: string) {
  const existing = document.querySelector('.eden-info-modal');
  if(existing) return;
  const wrap = document.createElement('div');
  wrap.className = 'eden-modal-overlay eden-info-modal';
  const card = document.createElement('div');
  card.className = 'eden-modal';
  const h = document.createElement('div');
  h.className = 'eden-modal-title';
  h.textContent = title;
  const p = document.createElement('div');
  p.className = 'eden-modal-text';
  p.textContent = text;
  const ok = document.createElement('button');
  ok.className = 'eden-chip eden-chip-primary';
  ok.textContent = t('Eden.Close', '关闭');
  ok.addEventListener('click', () => wrap.remove());
  const actions = document.createElement('div');
  actions.className = 'eden-modal-actions';
  actions.appendChild(ok);
  card.appendChild(h); card.appendChild(p); card.appendChild(actions);
  wrap.appendChild(card);
  wrap.addEventListener('click', (e) => { if(e.target === wrap) wrap.remove(); });
  document.body.appendChild(wrap);
}

export function installApiGuards() {
  const managers = (rootScope as any).managers;
  if(!managers || !managers.apiManager) {
    setTimeout(() => installApiGuards(), 800);
    return;
  }

  // Patch every facade that exposes invokeApi (the app uses different
  // instances before/after login and inside the worker bridge).
  const candidates: any[] = [];
  const push = (o: any) => { if(o && typeof o === 'object') candidates.push(o); };
  push(managers.apiManager);
  push(managers.apiManagerProxy);
  try {
    Object.keys(managers).forEach((k) => {
      const v = managers[k];
      if(v && typeof v === 'object' && typeof v.invokeApi === 'function') candidates.push(v);
    });
  } catch(e) {}

  candidates.forEach((obj) => {
    if(patchedApis.has(obj)) return;
    patchedApis.add(obj);
    ['invokeApi', 'invokeApiSingleProcess', 'invokeApiCacheable'].forEach((name) => {
      const orig = obj[name];
      if(typeof orig !== 'function') return;
      obj[name] = function(method: string, ...args: any[]) {
        if(typeof method === 'string' && UNSUPPORTED.includes(method)) {
          showUnsupportedModal(method);
          return Promise.reject(Object.assign(new Error('UNSUPPORTED'), {type: 'UNSUPPORTED'}));
        }
        return orig.call(obj, method, ...args);
      };
    });
  });

  const chats = managers.appChatsManager;
  if(chats && !patchedChats.has(chats)) {
    patchedChats.add(chats);

    // EdenDate: solo groups are supported now — no member check needed

    if(typeof chats.createChannel === 'function') {
      chats.createChannel = function() {
        showUnsupportedModal('channels.createChannel');
        return Promise.reject(Object.assign(new Error('UNSUPPORTED'), {type: 'UNSUPPORTED'}));
      };
    }
  }
}

